/**
 * #1106 — Transient false "stream proxy not reachable" from Connector pre-play probe.
 * Retry the same track before skip / failure UI (web mitigation).
 */
import { diagnosticLog } from '../utils/diagnosticLogger';

export const PROXY_UNREACHABLE_MAX_ATTEMPTS = 3;

export function isStreamProxyUnreachableError(message: string): boolean {
  const msg = String(message || '');
  return (
    msg.includes('Stream proxy on port')
    && msg.includes('is not reachable on this PC')
  );
}

/** Delay before attempt 2 and 3 after failures 1 and 2. */
export function proxyUnreachableRetryDelayMs(failedAttempt: number): number | null {
  if (failedAttempt === 1) return 5_000;
  if (failedAttempt === 2) return 10_000;
  return null;
}

export type ProxyUnreachableRetryOutcome = 'failure' | 'success' | 'exhausted';

export function logProxyUnreachableRetry(
  attempt: number,
  outcome: ProxyUnreachableRetryOutcome,
  extra?: Record<string, unknown>,
): void {
  diagnosticLog('info', 'playback', 'proxy_unreachable_retry', {
    attempt,
    outcome,
    ...extra,
  });
}

export async function delayBeforeProxyUnreachableRetry(failedAttempt: number): Promise<boolean> {
  if (failedAttempt >= PROXY_UNREACHABLE_MAX_ATTEMPTS) {
    logProxyUnreachableRetry(failedAttempt, 'exhausted');
    return false;
  }
  const delayMs = proxyUnreachableRetryDelayMs(failedAttempt);
  if (delayMs == null) {
    logProxyUnreachableRetry(failedAttempt, 'exhausted');
    return false;
  }
  logProxyUnreachableRetry(failedAttempt, 'failure', { next_delay_ms: delayMs });
  await new Promise((resolve) => setTimeout(resolve, delayMs));
  return true;
}

export function noteProxyUnreachableRecovery(attempt?: number): void {
  if (attempt != null && attempt > 1) {
    logProxyUnreachableRetry(attempt, 'success');
  }
}

export async function scheduleStreamingProxyUnreachableRetry(
  errMsg: string,
  failedAttempt: number,
  releaseInFlight: () => void,
  replay: (nextAttempt: number) => Promise<void>,
): Promise<boolean> {
  if (!isStreamProxyUnreachableError(errMsg)) {
    return false;
  }
  if (!(await delayBeforeProxyUnreachableRetry(failedAttempt))) {
    return false;
  }
  releaseInFlight();
  await replay(failedAttempt + 1);
  return true;
}

export async function scheduleLocalProxyUnreachableRetry(
  errMsg: string,
  failedAttempt: number,
  replay: () => Promise<void>,
): Promise<boolean> {
  if (!isStreamProxyUnreachableError(errMsg)) {
    return false;
  }
  if (!(await delayBeforeProxyUnreachableRetry(failedAttempt))) {
    return false;
  }
  await replay();
  return true;
}
